import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ownersAPI } from '../services/api';

const initialFormData = {
  type: 'Pessoa Física',
  name: '',
  cpf: '',
  rg: '',
  cnpj: '',
  companyName: '',
  stateRegistration: '',
  email: '',
  phone: '',
  whatsapp: '',
  address: {
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: ''
  },
  bankData: {
    bank: '',
    agency: '',
    account: '',
    accountType: '',
    pixKey: ''
  },
  notes: ''
};

const states = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

function OwnerForm() {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  useEffect(() => {
    if (isEditing) {
      loadOwner();
    }
  }, [id]);

  const loadOwner = async () => {
    setLoadingData(true);
    try {
      const response = await ownersAPI.getById(id);
      setFormData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar dados do proprietário');
      navigate('/proprietarios');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditing) {
        await ownersAPI.update(id, formData);
        toast.success('Proprietário atualizado com sucesso!');
      } else {
        await ownersAPI.create(formData);
        toast.success('Proprietário cadastrado com sucesso!');
      }
      navigate('/proprietarios');
    } catch (error) {
      const message = error.response?.data?.message || 
                      error.response?.data?.errors?.[0]?.msg ||
                      'Erro ao salvar proprietário';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            {isEditing ? 'Editar Proprietário' : 'Novo Proprietário'}
          </h1>
          <p className="page-subtitle">
            {isEditing ? 'Atualize os dados do proprietário' : 'Preencha os dados do proprietário do imóvel'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card">
          <div className="card-body">
            {/* Tipo de Pessoa */}
            <div className="form-section">
              <h3 className="form-section-title">👤 Tipo de Cadastro</h3>
              <div className="form-group">
                <label className="form-label required">Tipo de Pessoa</label>
                <select
                  name="type"
                  className="form-select"
                  value={formData.type}
                  onChange={handleChange}
                  required
                >
                  <option value="Pessoa Física">Pessoa Física</option>
                  <option value="Pessoa Jurídica">Pessoa Jurídica</option>
                </select>
              </div>
            </div>

            {/* Dados Pessoais */}
            <div className="form-section">
              <h3 className="form-section-title">
                {formData.type === 'Pessoa Física' ? '📋 Dados Pessoais' : '🏢 Dados da Empresa'}
              </h3>
              
              {formData.type === 'Pessoa Física' ? (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label required">Nome Completo</label>
                      <input
                        type="text"
                        name="name"
                        className="form-input"
                        placeholder="Nome completo"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">CPF</label>
                      <input
                        type="text"
                        name="cpf"
                        className="form-input"
                        placeholder="000.000.000-00"
                        value={formData.cpf}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">RG</label>
                      <input
                        type="text"
                        name="rg"
                        className="form-input"
                        placeholder="Número do RG"
                        value={formData.rg}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label required">Razão Social</label>
                      <input
                        type="text"
                        name="name"
                        className="form-input"
                        placeholder="Razão social da empresa"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Nome Fantasia</label>
                      <input
                        type="text"
                        name="companyName"
                        className="form-input"
                        placeholder="Nome fantasia"
                        value={formData.companyName}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">CNPJ</label>
                      <input
                        type="text"
                        name="cnpj"
                        className="form-input"
                        placeholder="00.000.000/0000-00"
                        value={formData.cnpj}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Inscrição Estadual</label>
                      <input
                        type="text"
                        name="stateRegistration"
                        className="form-input"
                        placeholder="Inscrição estadual"
                        value={formData.stateRegistration}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Contato */}
            <div className="form-section">
              <h3 className="form-section-title">📞 Contato</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label required">E-mail</label>
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    placeholder="email@exemplo.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label required">Telefone</label>
                  <input
                    type="tel"
                    name="phone"
                    className="form-input"
                    placeholder="(00) 00000-0000"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">WhatsApp</label>
                  <input
                    type="tel"
                    name="whatsapp"
                    className="form-input"
                    placeholder="(00) 00000-0000"
                    value={formData.whatsapp}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            {/* Endereço */}
            <div className="form-section">
              <h3 className="form-section-title">📍 Endereço</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">CEP</label>
                  <input
                    type="text"
                    name="address.zipCode"
                    className="form-input"
                    placeholder="00000-000"
                    value={formData.address?.zipCode || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label className="form-label">Logradouro</label>
                  <input
                    type="text"
                    name="address.street"
                    className="form-input"
                    placeholder="Rua, Avenida, etc."
                    value={formData.address?.street || ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Número</label>
                  <input
                    type="text"
                    name="address.number"
                    className="form-input"
                    placeholder="Nº"
                    value={formData.address?.number || ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Complemento</label>
                  <input
                    type="text"
                    name="address.complement"
                    className="form-input"
                    placeholder="Apto, Sala, etc."
                    value={formData.address?.complement || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Bairro</label>
                  <input
                    type="text"
                    name="address.neighborhood"
                    className="form-input"
                    placeholder="Bairro"
                    value={formData.address?.neighborhood || ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Cidade</label>
                  <input
                    type="text"
                    name="address.city"
                    className="form-input"
                    placeholder="Cidade"
                    value={formData.address?.city || ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Estado</label>
                  <select
                    name="address.state"
                    className="form-select"
                    value={formData.address?.state || ''}
                    onChange={handleChange}
                  >
                    <option value="">Selecione</option>
                    {states.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Dados Bancários */}
            <div className="form-section">
              <h3 className="form-section-title">🏦 Dados Bancários</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Banco</label>
                  <input
                    type="text"
                    name="bankData.bank"
                    className="form-input"
                    placeholder="Nome do banco"
                    value={formData.bankData?.bank || ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Agência</label>
                  <input
                    type="text"
                    name="bankData.agency"
                    className="form-input"
                    placeholder="Número da agência"
                    value={formData.bankData?.agency || ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Conta</label>
                  <input
                    type="text"
                    name="bankData.account"
                    className="form-input"
                    placeholder="Número da conta"
                    value={formData.bankData?.account || ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Tipo de Conta</label>
                  <select
                    name="bankData.accountType"
                    className="form-select"
                    value={formData.bankData?.accountType || ''}
                    onChange={handleChange}
                  >
                    <option value="">Selecione</option>
                    <option value="Corrente">Corrente</option>
                    <option value="Poupança">Poupança</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Chave PIX</label>
                <input
                  type="text"
                  name="bankData.pixKey"
                  className="form-input"
                  placeholder="CPF, CNPJ, E-mail, Telefone ou Chave Aleatória"
                  value={formData.bankData?.pixKey || ''}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Observações */}
            <div className="form-section">
              <h3 className="form-section-title">📝 Observações</h3>
              <div className="form-group">
                <textarea
                  name="notes"
                  className="form-textarea"
                  placeholder="Observações adicionais sobre o proprietário..."
                  value={formData.notes}
                  onChange={handleChange}
                  rows={4}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/proprietarios')}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Salvando...' : (isEditing ? 'Salvar Alterações' : 'Cadastrar Proprietário')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default OwnerForm;
